import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useServices } from '../../../contexts/ServicesContext';

interface SolutionsSectionProps {
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
}

interface ApiService {
  _id: string;
  name: string;
  shortDescription?: string;
  description?: string;
  icon?: string;
  type: 'service' | 'solution' | 'academy';
  isActive?: boolean;
  isFeatured?: boolean;
  order?: number;
  parentId?: string | null;
  slug?: string;
  features?: string[];
  featuredImage?: string | null;
}

export default function SolutionsSection({
  isVisible,
  sectionRef,
  name = 'Giải Pháp Của Chúng Tôi',
  description = 'Các giải pháp công nghệ hàng đầu cho doanh nghiệp',
  background,
  nameColor,
  descriptionColor
}: SolutionsSectionProps) {
  const { solutions: contextSolutions } = useServices();
  const [featuredSolutions, setFeaturedSolutions] = useState<ApiService[]>([]);

  // Lấy data từ context (đã được Header fetch)
  useEffect(() => {
    // Lấy 2 solutions đầu tiên
    const featured = contextSolutions
      .filter((s: ApiService) => s.type === 'solution')
      .sort((a: ApiService, b: ApiService) => (a.order || 0) - (b.order || 0))
      .slice(0, 2);
    setFeaturedSolutions(featured);
    
  }, [contextSolutions]);

  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <section 
      ref={sectionRef}
      data-section="solutions"
      className={`py-12 sm:py-14 md:py-16 lg:py-20 relative overflow-hidden ${!background ? 'bg-white' : ''}`}
      style={backgroundStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Solutions Section Header */}
        {(name || description) && (
          <div className={`text-center mb-8 sm:mb-10 md:mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {name && (
              <h2 
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 md:mb-5 leading-[1.2] tracking-[-0.02em] px-4"
                style={{ color: nameColor || '#2a4b7c' }}
              >
                {name}
              </h2>
            )}
            {description && (
              <p 
                className="text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-[1.7] font-normal px-4"
                style={{ color: descriptionColor || '#64748b' }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Featured Solutions Section - Side by Side */}
        {featuredSolutions.length > 0 ? (
          <div className="mb-0">
            <div className={`space-y-10 sm:space-y-12 md:space-y-16 lg:space-y-20 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            } transition-all duration-1000`}>
              {featuredSolutions.map((solution, index) => {
                // Lấy features từ API, nếu không có thì dùng default
                const features = solution.features && solution.features.length > 0 
                  ? solution.features.slice(0, 3) // Limit 3 features
                  : [
                      'Tự động hóa quy trình',
                      'Tích hợp đa kênh',
                      'Phân tích dữ liệu bằng AI'
                    ];
                
                // Alternating layout: index 0 = text left/image right, index 1 = image left/text right
                const isEven = index % 2 === 0;
                
                return (
                  <div
                    key={solution._id}
                    className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-center"
                  >
                    {/* Content Section */}
                    <div className={`flex-1 ${isEven ? 'md:order-1' : 'md:order-2'}`}>
                      {/* Badge */}
                      <div className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#06b6d4]/10 text-[#06b6d4] text-[10px] sm:text-xs font-bold uppercase rounded-full mb-3 sm:mb-4">
                        GIẢI PHÁP
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#2a4b7c] mb-3 sm:mb-4 leading-tight">
                        {solution.name}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-xs sm:text-sm md:text-base text-[#64748b] mb-4 sm:mb-5 md:mb-6 leading-relaxed">
                        {solution.shortDescription || solution.description}
                      </p>
                      
                      {/* Features List */}
                      <div className="space-y-2 sm:space-y-2.5 mb-5 sm:mb-6">
                        {features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 sm:gap-3">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center bg-[#00c897] rounded-full flex-shrink-0">
                              <i className="ri-check-line text-[10px] sm:text-xs text-white"></i>
                            </div>
                            <span className="text-xs sm:text-sm text-[#64748b]">{feature.trim()}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* CTA Button */}
                      <Link
                        to={solution.slug ? `/giai-phap/${solution.slug}` : '/giai-phap-va-dich-vu'}
                        className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-[#2a4b7c] text-white text-xs sm:text-sm font-bold rounded-lg hover:bg-[#1e3a5f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                      >
                        Tìm hiểu chi tiết
                        <i className="ri-arrow-right-line text-xs sm:text-sm"></i>
                      </Link>
                    </div>
                    
                    {/* Visual Section */}
                    <div className={`flex-shrink-0 ${isEven ? 'md:order-2' : 'md:order-1'}`}>
                      <div className="relative rounded-lg sm:rounded-xl overflow-hidden shadow-lg bg-gray-50 p-3 sm:p-4">
                        {solution.featuredImage ? (
                          <img
                            src={solution.featuredImage}
                            alt={solution.name}
                            className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-40 sm:h-48 md:h-56 lg:h-64 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <i className={`${solution.icon || 'ri-stack-line'} text-4xl sm:text-5xl md:text-6xl text-white/20 mb-1 sm:mb-2`}></i>
                              <p className="text-white/40 text-[10px] sm:text-xs font-semibold">{solution.name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-600">Chưa có giải pháp nào</p>
          </div>
        )}
      </div>
    </section>
  );
}
